import type { ConfluenceHeaderImageGenerationResponse } from '@atlassian/convo-ai-api';

export type AIHeaderImageStatus =
	| 'NOT_LOADING'
	| 'LOADING'
	| 'LOADING_WITH_ACCEPTANCE_FLOW'
	| 'SUCCESSFULLY_SET'
	| 'ERROR';

/**
 * Available theme values for AI header image generation
 */
export const IMAGE_GENERATION_THEMES = {
	photorealistic: 'photorealistic',
	abstract: 'abstract',
	watercolors: 'watercolors',
	graphic_design: 'graphic_design',
	landscape: 'landscape',
} as const;

export type ImageGenerationTheme =
	(typeof IMAGE_GENERATION_THEMES)[keyof typeof IMAGE_GENERATION_THEMES];

export type GenerateImageProps = {
	adfContent: string;
	aspectRatio?: string;
	showAcceptanceFlow?: boolean;
	theme?: ImageGenerationTheme;
	useRawPrompt?: boolean;
};

export type GenerateImageEventCallbacks = {
	onGenerateAbort?: (params: {
		aspectRatio?: string;
		contentId: string;
		theme?: string;
		useRawPrompt: boolean;
	}) => void;
	onGenerateError?: (params: {
		aspectRatio?: string;
		contentId: string;
		error: Error;
		theme?: string;
		useRawPrompt: boolean;
	}) => void;
	onGenerateStart?: (params: {
		aspectRatio?: string;
		contentId: string;
		theme?: string;
		useRawPrompt: boolean;
	}) => void;
	onGenerateSuccess?: (params: {
		aspectRatio?: string;
		contentId: string;
		theme?: string;
		useRawPrompt: boolean;
	}) => void;
};

export type UseAIGeneratedCoverImageProps = {
	contentId: string;
	/**
	 * Optional callbacks for tracking image generation events (e.g., experience tracking)
	 */
	eventCallbacks?: GenerateImageEventCallbacks;
};

export type UseAIGeneratedCoverImageReturn = {
	aiHeaderImageFileId?: string;
	aiHeaderImageStatus?: AIHeaderImageStatus;
	data: ConfluenceHeaderImageGenerationResponse | null;
	error: string | null;
	fakeGenerateImage: () => void;
	generateImage: (props: GenerateImageProps) => Promise<void>;
	loading: boolean;
	setAIHeaderImageStatus: (status: AIHeaderImageStatus) => void;
};
